import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Zap, Package, Pill, FileText, AlertTriangle, CheckCircle,
  ShoppingCart, Truck, DollarSign, Users, Activity,
  Stethoscope, Clipboard, Plus, ChevronRight, Shield,
  TrendingUp, AlertCircle, Sparkles, BedDouble, Droplets
} from "lucide-react";

const OMSAutomationPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('triggers');
  const [loading, setLoading] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const [supportedTriggers, setSupportedTriggers] = useState([]);
  const [supplyPackages, setSupplyPackages] = useState([]);
  const [orderGradeResult, setOrderGradeResult] = useState(null);
  const [resupplyResult, setResupplyResult] = useState(null);
  const [idgSummary, setIdgSummary] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  
  // Form state for trigger analysis
  const [triggerForm, setTriggerForm] = useState({
    chart_id: 'chart_demo',
    patient_id: 'patient_demo',
    source: 'hope_assessment',
    finding_type: '',
    finding_detail: '',
    raw_text: '',
    documented_by: user?.name || 'Staff'
  });
  
  // Form state for order grading
  const [orderGradeForm, setOrderGradeForm] = useState({
    order_type: 'medication',
    order_name: '',
    order_content: '',
    patient_diagnoses: '',
    poc_goals: '',
    assessment_findings: ''
  });
  
  // Form state for resupply
  const [resupplyForm, setResupplyForm] = useState({
    chart_id: 'chart_demo',
    patient_id: 'patient_demo',
    diagnoses: '',
    current_supplies: ''
  });

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'administrator', 'staff'].includes(user.role))) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    loadSupportedTriggers();
    loadSupplyPackages();
  }, []);

  const loadSupportedTriggers = async () => {
    try {
      const response = await api.get('/ehr/oms/triggers/supported');
      setSupportedTriggers(response.data.triggers || []);
    } catch (error) {
      console.error("Failed to load triggers:", error);
    }
  };

  const loadSupplyPackages = async () => {
    try {
      const response = await api.get('/ehr/oms/supplies/packages');
      setSupplyPackages(response.data.packages || []);
    } catch (error) {
      console.error("Failed to load supply packages:", error);
    }
  };

  const handleAnalyzeTrigger = async () => {
    if (!triggerForm.raw_text.trim()) {
      toast.error("Please enter clinical documentation to analyze");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ehr/oms/trigger/analyze', {
        chart_id: triggerForm.chart_id,
        patient_id: triggerForm.patient_id,
        source: triggerForm.source,
        finding_type: triggerForm.finding_type || 'assessment',
        finding_detail: triggerForm.finding_detail || 'Clinical finding',
        raw_text: triggerForm.raw_text,
        documented_by: triggerForm.documented_by
      });
      
      setTriggerResult(response.data);
      setSelectedOrders(response.data.triggered_orders.map(o => o.order_id));
      
      if (response.data.triggered_orders.length > 0) {
        toast.success(`Detected ${response.data.triggered_orders.length} automated orders!`);
        setShowOrderModal(true);
      } else {
        toast.info("No clinical triggers detected in documentation");
      }
    } catch (error) {
      console.error("Trigger analysis failed:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze triggers");
    } finally {
      setLoading(false);
    }
  };

  const handleGradeOrder = async () => {
    if (!orderGradeForm.order_name || !orderGradeForm.order_content) {
      toast.error("Please enter order name and content");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ehr/oms/order/grade', {
        order_type: orderGradeForm.order_type,
        order_name: orderGradeForm.order_name,
        order_content: orderGradeForm.order_content,
        patient_diagnoses: orderGradeForm.patient_diagnoses.split(',').map(d => d.trim()).filter(d => d),
        poc_goals: orderGradeForm.poc_goals.split(',').map(g => g.trim()).filter(g => g),
        assessment_findings: orderGradeForm.assessment_findings.split(',').map(f => f.trim()).filter(f => f)
      });
      
      setOrderGradeResult(response.data);
      
      if (response.data.grade === 'A' || response.data.grade === 'B') {
        toast.success(`Order Grade: ${response.data.grade} - ${response.data.compliance_status}`);
      } else if (response.data.grade === 'F') {
        toast.error(`Order Grade: F - ${response.data.compliance_status}`);
      } else {
        toast.warning(`Order Grade: ${response.data.grade} - ${response.data.compliance_status}`);
      }
    } catch (error) {
      console.error("Order grading failed:", error);
      toast.error("Failed to grade order");
    } finally {
      setLoading(false);
    }
  };

  const handleResupplySuggest = async () => {
    setLoading(true);
    try {
      const response = await api.post('/ehr/oms/resupply/suggest', {
        chart_id: resupplyForm.chart_id,
        patient_id: resupplyForm.patient_id,
        diagnoses: resupplyForm.diagnoses.split(',').map(d => d.trim()).filter(d => d) || ['general'],
        current_supplies: resupplyForm.current_supplies.split(',').map(s => s.trim()).filter(s => s)
      });
      
      setResupplyResult(response.data);
      toast.success(`Suggested ${response.data.total_items} items for resupply`);
    } catch (error) {
      console.error("Resupply suggestion failed:", error);
      toast.error("Failed to generate resupply suggestions");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveResupply = async () => {
    if (!resupplyResult) return;
    
    setLoading(true);
    try {
      const response = await api.post(`/ehr/oms/resupply/approve/${resupplyResult.cart_id}`, null, {
        params: { approved_by: user?.name || 'Staff' }
      });
      
      toast.success("Resupply order approved and submitted!");
      setResupplyResult(null);
    } catch (error) {
      console.error("Resupply approval failed:", error);
      toast.error("Failed to approve resupply");
    } finally {
      setLoading(false);
    }
  };

  const loadIDGSummary = async (patientId) => {
    try {
      const response = await api.get(`/ehr/oms/idg/supply-summary/${patientId}`);
      setIdgSummary(response.data);
    } catch (error) {
      console.error("Failed to load IDG summary:", error);
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'bg-green-100 text-green-700 border-green-300';
      case 'B': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'C': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'D': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'F': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getOrderTypeIcon = (type) => {
    switch (type) {
      case 'medication': return <Pill className="w-4 h-4" />;
      case 'dme': return <BedDouble className="w-4 h-4" />;
      case 'supply': return <Package className="w-4 h-4" />;
      case 'wound_care': return <Droplets className="w-4 h-4" />;
      case 'hha_task': return <Users className="w-4 h-4" />;
      case 'nursing_protocol': return <Clipboard className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const insertSampleText = (type) => {
    const samples = {
      wound: "HOPE Assessment Section M - Skin Integrity: Pressure ulcer, Stage 3, Coccyx. Area measures 3cm x 2cm x 0.5cm depth. Wound bed is 80% granulation, 20% slough. Moderate serous drainage. Surrounding skin intact but erythematous.",
      incontinence: "Functional Assessment: Patient is incontinent of bowel and bladder. Requires total assist with toileting. Currently using adult briefs, changed approximately 4-5 times daily.",
      dyspnea: "Symptom Assessment: Severe dyspnea at rest. Oxygen saturation 88% on room air. Patient unable to speak in full sentences. Using accessory muscles for breathing.",
      bedbound: "Mobility Assessment: Patient is bedbound, unable to get out of bed without maximum assist of two. Cannot bear weight. Requires hospital bed with side rails."
    };
    setTriggerForm(prev => ({ ...prev, raw_text: samples[type] || '' }));
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-orange-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Zap className="w-8 h-8 text-orange-600" />
                Automated OMS Interface
              </h1>
              <p className="text-muted-foreground mt-1">
                Trigger-Based Logistics: Clinical documentation drives automated orders, supplies & DME
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/ehr-compliance')}>
              Back to EHR
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Triggers</p>
                  <p className="text-2xl font-bold">{supportedTriggers.length}</p>
                </div>
                <Zap className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Supply Packages</p>
                  <p className="text-2xl font-bold">{supplyPackages.length}</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Auto-Link Rate</p>
                  <p className="text-2xl font-bold">98%</p>
                </div>
                <Shield className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Time Saved</p>
                  <p className="text-2xl font-bold">12 min</p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="triggers" data-testid="tab-triggers">
              <Zap className="w-4 h-4 mr-2" />
              Logic-Link Triggers
            </TabsTrigger>
            <TabsTrigger value="grading" data-testid="tab-grading">
              <Shield className="w-4 h-4 mr-2" />
              Order Grading
            </TabsTrigger>
            <TabsTrigger value="resupply" data-testid="tab-resupply">
              <ShoppingCart className="w-4 h-4 mr-2" />
              One-Click Resupply
            </TabsTrigger>
            <TabsTrigger value="idg" data-testid="tab-idg">
              <Users className="w-4 h-4 mr-2" />
              IDG Cost Summary
            </TabsTrigger>
          </TabsList>

          {/* Logic-Link Triggers Tab */}
          <TabsContent value="triggers">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-orange-600" />
                      Clinical Documentation Input
                    </CardTitle>
                    <CardDescription>
                      Enter clinical findings - the system will automatically generate orders, DME, and supplies
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Documentation Source</Label>
                        <Select 
                          value={triggerForm.source}
                          onValueChange={(value) => setTriggerForm(prev => ({ ...prev, source: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hope_assessment">HOPE Assessment</SelectItem>
                            <SelectItem value="functional_assessment">Functional Assessment</SelectItem>
                            <SelectItem value="symptom_assessment">Symptom Assessment</SelectItem>
                            <SelectItem value="visit_note">Visit Note</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Sample Triggers</Label>
                        <Select onValueChange={(value) => insertSampleText(value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Insert sample..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="wound">Stage 3 Pressure Ulcer</SelectItem>
                            <SelectItem value="incontinence">Double Incontinence</SelectItem>
                            <SelectItem value="dyspnea">Severe Dyspnea</SelectItem>
                            <SelectItem value="bedbound">Bedbound Status</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Clinical Documentation</Label>
                      <Textarea
                        value={triggerForm.raw_text}
                        onChange={(e) => setTriggerForm(prev => ({ ...prev, raw_text: e.target.value }))}
                        placeholder="Enter clinical documentation... e.g., 'Pressure ulcer, Stage 3, Coccyx' or 'Patient is incontinent of bowel and bladder'"
                        className="min-h-[200px]"
                        data-testid="trigger-input"
                      />
                    </div>

                    <Button 
                      onClick={handleAnalyzeTrigger}
                      disabled={loading || !triggerForm.raw_text.trim()}
                      className="w-full"
                      data-testid="analyze-trigger-btn"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      Analyze & Generate Orders
                    </Button>
                  </CardContent>
                </Card>

                {/* Trigger Results */}
                {triggerResult && (
                  <Card className="border-orange-200">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-orange-700">
                        <CheckCircle className="w-5 h-5" />
                        Generated Orders & Supplies
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="p-3 bg-orange-50 rounded-lg">
                        <p className="text-sm text-orange-700">{triggerResult.compliance_message}</p>
                        {triggerResult.total_estimated_cost && (
                          <p className="text-sm font-medium text-orange-800 mt-1">
                            Estimated Cost: ${triggerResult.total_estimated_cost.toFixed(2)}
                          </p>
                        )}
                      </div>

                      {triggerResult.md_notification_required && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                          <span className="text-sm text-red-700 font-medium">MD Notification Required</span>
                        </div>
                      )}

                      {triggerResult.loc_review_suggested && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-700 font-medium">LOC Review Suggested:</p>
                          <p className="text-sm text-amber-600">{triggerResult.loc_review_suggested}</p>
                        </div>
                      )}

                      {/* Orders List */}
                      <div className="space-y-2">
                        <h4 className="font-medium">Triggered Orders ({triggerResult.triggered_orders.length})</h4>
                        {triggerResult.triggered_orders.map((order, idx) => (
                          <div key={idx} className="p-3 border rounded-lg flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${
                              order.order_type === 'medication' ? 'bg-purple-100' :
                              order.order_type === 'supply' ? 'bg-blue-100' :
                              order.order_type === 'wound_care' ? 'bg-red-100' :
                              order.order_type === 'hha_task' ? 'bg-green-100' :
                              'bg-gray-100'
                            }`}>
                              {getOrderTypeIcon(order.order_type)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-medium">{order.name}</p>
                                <Badge className={getGradeColor(order.grade)}>Grade {order.grade}</Badge>
                              </div>
                              {order.content && <p className="text-sm text-muted-foreground mt-1">{order.content}</p>}
                              {order.items.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {order.items.map((item, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{item}</Badge>
                                  ))}
                                </div>
                              )}
                              <p className="text-xs text-green-600 mt-2">
                                <Shield className="w-3 h-3 inline mr-1" />
                                {order.compliance_link}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* DME Orders */}
                      {triggerResult.dme_orders.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium">DME Orders ({triggerResult.dme_orders.length})</h4>
                          {triggerResult.dme_orders.map((dme, idx) => (
                            <div key={idx} className="p-3 border rounded-lg bg-indigo-50">
                              <div className="flex items-center gap-2">
                                <BedDouble className="w-5 h-5 text-indigo-600" />
                                <span className="font-medium">{dme.name}</span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">{dme.justification}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* HHA Tasks */}
                      {triggerResult.hha_tasks.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium">HHA Care Plan Updates ({triggerResult.hha_tasks.length})</h4>
                          {triggerResult.hha_tasks.map((task, idx) => (
                            <div key={idx} className="p-3 border rounded-lg bg-green-50">
                              <div className="flex items-center gap-2">
                                <Users className="w-5 h-5 text-green-600" />
                                <span className="font-medium">{task.task_name}</span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">{task.task_detail}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Sidebar - Supported Triggers */}
              <div>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Supported Clinical Triggers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {supportedTriggers.map((trigger, idx) => (
                        <div key={idx} className="p-2 border rounded-lg text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{trigger.trigger_key.replace(/_/g, ' ')}</span>
                            <Badge variant="outline" className="text-xs">{trigger.category}</Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <span>{trigger.order_count} orders</span>
                            {trigger.has_dme && <Badge variant="secondary" className="text-xs">+DME</Badge>}
                            {trigger.requires_md_notification && <Badge variant="destructive" className="text-xs">MD</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Order Grading Tab */}
          <TabsContent value="grading">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Order Compliance Grading</CardTitle>
                  <CardDescription>
                    Grade orders A-F based on clinical justification linkage
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Order Type</Label>
                      <Select 
                        value={orderGradeForm.order_type}
                        onValueChange={(value) => setOrderGradeForm(prev => ({ ...prev, order_type: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="medication">Medication</SelectItem>
                          <SelectItem value="dme">DME</SelectItem>
                          <SelectItem value="supply">Supply</SelectItem>
                          <SelectItem value="nursing_protocol">Nursing Protocol</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Order Name</Label>
                      <Input
                        value={orderGradeForm.order_name}
                        onChange={(e) => setOrderGradeForm(prev => ({ ...prev, order_name: e.target.value }))}
                        placeholder="e.g., Albuterol nebulizer"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Order Content/Details</Label>
                    <Textarea
                      value={orderGradeForm.order_content}
                      onChange={(e) => setOrderGradeForm(prev => ({ ...prev, order_content: e.target.value }))}
                      placeholder="e.g., Albuterol 2.5mg nebulizer Q4H PRN wheezing"
                    />
                  </div>

                  <div>
                    <Label>Patient Diagnoses (comma-separated)</Label>
                    <Input
                      value={orderGradeForm.patient_diagnoses}
                      onChange={(e) => setOrderGradeForm(prev => ({ ...prev, patient_diagnoses: e.target.value }))}
                      placeholder="e.g., COPD, Terminal Lung Cancer"
                    />
                  </div>

                  <div>
                    <Label>POC Goals (comma-separated)</Label>
                    <Input
                      value={orderGradeForm.poc_goals}
                      onChange={(e) => setOrderGradeForm(prev => ({ ...prev, poc_goals: e.target.value }))}
                      placeholder="e.g., Patient will have respiratory distress managed"
                    />
                  </div>

                  <div>
                    <Label>Today's Assessment Findings (comma-separated)</Label>
                    <Input
                      value={orderGradeForm.assessment_findings}
                      onChange={(e) => setOrderGradeForm(prev => ({ ...prev, assessment_findings: e.target.value }))}
                      placeholder="e.g., Wheezing noted on auscultation, Dyspnea level 3"
                    />
                  </div>

                  <Button 
                    onClick={handleGradeOrder}
                    disabled={loading}
                    className="w-full"
                    data-testid="grade-order-btn"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Shield className="w-4 h-4 mr-2" />
                    )}
                    Grade Order
                  </Button>
                </CardContent>
              </Card>

              {/* Grade Result */}
              <div className="space-y-4">
                {orderGradeResult ? (
                  <Card className={`border-2 ${
                    orderGradeResult.grade === 'A' ? 'border-green-400' :
                    orderGradeResult.grade === 'B' ? 'border-blue-400' :
                    orderGradeResult.grade === 'F' ? 'border-red-400' :
                    'border-yellow-400'
                  }`}>
                    <CardContent className="pt-6">
                      <div className="text-center mb-6">
                        <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full border-4 ${getGradeColor(orderGradeResult.grade)}`}>
                          <span className="text-5xl font-bold">{orderGradeResult.grade}</span>
                        </div>
                        <p className="text-lg font-semibold mt-3">{orderGradeResult.compliance_status}</p>
                      </div>

                      <div className="space-y-3">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm font-medium">Rationale</p>
                          <p className="text-sm text-muted-foreground">{orderGradeResult.grade_rationale}</p>
                        </div>

                        {orderGradeResult.linked_diagnosis && (
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-sm font-medium text-green-700">Linked Diagnosis</p>
                            <p className="text-sm text-green-600">{orderGradeResult.linked_diagnosis}</p>
                          </div>
                        )}

                        {orderGradeResult.linked_poc_goal && (
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm font-medium text-blue-700">Linked POC Goal</p>
                            <p className="text-sm text-blue-600">{orderGradeResult.linked_poc_goal}</p>
                          </div>
                        )}

                        {orderGradeResult.warnings.length > 0 && (
                          <div className="p-3 bg-amber-50 rounded-lg">
                            <p className="text-sm font-medium text-amber-700">Warnings</p>
                            {orderGradeResult.warnings.map((w, i) => (
                              <p key={i} className="text-sm text-amber-600 flex items-start gap-1 mt-1">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                {w}
                              </p>
                            ))}
                          </div>
                        )}

                        {orderGradeResult.required_actions.length > 0 && (
                          <div className="p-3 bg-red-50 rounded-lg">
                            <p className="text-sm font-medium text-red-700">Required Actions</p>
                            <ul className="list-disc list-inside text-sm text-red-600 mt-1">
                              {orderGradeResult.required_actions.map((a, i) => (
                                <li key={i}>{a}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                      <p className="text-muted-foreground">
                        Enter order details to check compliance grading
                      </p>
                      <div className="mt-4 text-xs text-muted-foreground text-left">
                        <p className="font-medium mb-2">Grading Criteria:</p>
                        <div className="space-y-1">
                          <p><Badge className={getGradeColor('A')}>A</Badge> Order linked to diagnosis + POC goal</p>
                          <p><Badge className={getGradeColor('B')}>B</Badge> Order linked to diagnosis only</p>
                          <p><Badge className={getGradeColor('C')}>C</Badge> Clinical justification provided</p>
                          <p><Badge className={getGradeColor('D')}>D</Badge> Weak justification</p>
                          <p><Badge className={getGradeColor('F')}>F</Badge> No documented clinical need</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Resupply Tab */}
          <TabsContent value="resupply">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    One-Click Resupply
                  </CardTitle>
                  <CardDescription>
                    Generate predictive supply orders based on patient profile
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Patient Diagnoses (comma-separated)</Label>
                    <Input
                      value={resupplyForm.diagnoses}
                      onChange={(e) => setResupplyForm(prev => ({ ...prev, diagnoses: e.target.value }))}
                      placeholder="e.g., COPD, CHF, incontinence, wounds"
                    />
                  </div>

                  <div>
                    <Label>Current Supplies (comma-separated, optional)</Label>
                    <Input
                      value={resupplyForm.current_supplies}
                      onChange={(e) => setResupplyForm(prev => ({ ...prev, current_supplies: e.target.value }))}
                      placeholder="Items already ordered/in stock"
                    />
                  </div>

                  <Button 
                    onClick={handleResupplySuggest}
                    disabled={loading}
                    className="w-full"
                    data-testid="resupply-suggest-btn"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <Package className="w-4 h-4 mr-2" />
                    )}
                    Generate Resupply Cart
                  </Button>
                </CardContent>
              </Card>

              {/* Resupply Result */}
              {resupplyResult ? (
                <Card className="border-blue-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-blue-700">Suggested Resupply Cart</CardTitle>
                      <Badge variant="outline">{resupplyResult.total_items} items</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {resupplyResult.suggested_packages.map((pkg, idx) => (
                      <div key={idx} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium">{pkg.name}</span>
                          <Badge>${pkg.estimated_cost.toFixed(2)}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {pkg.items.map((item, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{item}</Badge>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">Estimated Total</span>
                        <span className="text-xl font-bold text-blue-700">${resupplyResult.estimated_cost.toFixed(2)}</span>
                      </div>
                      <Button 
                        onClick={handleApproveResupply}
                        disabled={loading}
                        className="w-full"
                      >
                        <Truck className="w-4 h-4 mr-2" />
                        Approve & Submit Order
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <h4 className="font-medium">Available Supply Packages</h4>
                      {supplyPackages.map((pkg, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <p className="font-medium">{pkg.name}</p>
                          <p className="text-xs text-muted-foreground">{pkg.item_count} items</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {pkg.items.slice(0, 4).map((item, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{item}</Badge>
                            ))}
                            {pkg.items.length > 4 && (
                              <Badge variant="outline" className="text-xs">+{pkg.items.length - 4} more</Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* IDG Cost Summary Tab */}
          <TabsContent value="idg">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      IDG Supply & Cost Summary
                    </CardTitle>
                    <CardDescription>
                      Real-time visibility into per-patient-day spend for IDG review
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex gap-4">
                      <Input placeholder="Enter Patient ID" className="flex-1" />
                      <Button onClick={() => loadIDGSummary('demo_patient')}>
                        Load Summary
                      </Button>
                    </div>

                    {idgSummary && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-4 bg-blue-50 rounded-lg text-center">
                            <p className="text-sm text-muted-foreground">Supply Cost</p>
                            <p className="text-2xl font-bold text-blue-700">${idgSummary.total_supply_cost.toFixed(2)}</p>
                          </div>
                          <div className="p-4 bg-indigo-50 rounded-lg text-center">
                            <p className="text-sm text-muted-foreground">DME Cost</p>
                            <p className="text-2xl font-bold text-indigo-700">${idgSummary.total_dme_cost.toFixed(2)}</p>
                          </div>
                          <div className="p-4 bg-purple-50 rounded-lg text-center">
                            <p className="text-sm text-muted-foreground">Medication Cost</p>
                            <p className="text-2xl font-bold text-purple-700">${idgSummary.total_medication_cost.toFixed(2)}</p>
                          </div>
                        </div>

                        <div className={`p-4 rounded-lg ${idgSummary.cost_change_percent > 25 ? 'bg-red-50' : 'bg-green-50'}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Cost Change This Period</span>
                            <span className={`text-xl font-bold ${idgSummary.cost_change_percent > 25 ? 'text-red-600' : 'text-green-600'}`}>
                              {idgSummary.cost_change_percent > 0 ? '+' : ''}{idgSummary.cost_change_percent}%
                            </span>
                          </div>
                        </div>

                        <div>
                          <h4 className="font-medium mb-2">Cost Drivers</h4>
                          {idgSummary.cost_drivers.map((driver, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 border-b">
                              <div>
                                <span className="font-medium">{driver.category}</span>
                                <p className="text-xs text-muted-foreground">{driver.reason}</p>
                              </div>
                              <span className="font-semibold">${driver.cost.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>

                        <div className="p-4 bg-amber-50 rounded-lg">
                          <h4 className="font-medium text-amber-700 mb-2">IDG Discussion Points</h4>
                          {idgSummary.recommendations.map((rec, idx) => (
                            <p key={idx} className="text-sm text-amber-600 mb-1">• {rec}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Cost Alerts Sidebar */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    High Cost Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="p-3 border rounded-lg border-red-200 bg-red-50">
                      <p className="font-medium">John Smith</p>
                      <p className="text-xs text-red-600">+40% cost increase</p>
                      <p className="text-xs text-muted-foreground">Wound care escalation</p>
                    </div>
                    <div className="p-3 border rounded-lg border-amber-200 bg-amber-50">
                      <p className="font-medium">Mary Johnson</p>
                      <p className="text-xs text-amber-600">+35% cost increase</p>
                      <p className="text-xs text-muted-foreground">New DME added</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Order Approval Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-600" />
              Confirm Generated Orders
            </DialogTitle>
            <DialogDescription>
              The following orders were automatically generated from your clinical documentation. Review and approve to add to the patient's chart.
            </DialogDescription>
          </DialogHeader>
          
          {triggerResult && (
            <div className="space-y-4">
              {triggerResult.triggered_orders.map((order, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg">
                  <Checkbox 
                    checked={selectedOrders.includes(order.order_id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedOrders(prev => [...prev, order.order_id]);
                      } else {
                        setSelectedOrders(prev => prev.filter(id => id !== order.order_id));
                      }
                    }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {getOrderTypeIcon(order.order_type)}
                      <span className="font-medium">{order.name}</span>
                      <Badge className={getGradeColor(order.grade)}>Grade {order.grade}</Badge>
                    </div>
                    {order.content && <p className="text-sm text-muted-foreground mt-1">{order.content}</p>}
                    <p className="text-xs text-green-600 mt-1">
                      <Shield className="w-3 h-3 inline mr-1" />
                      {order.compliance_link}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              toast.success(`${selectedOrders.length} orders approved and added to chart`);
              setShowOrderModal(false);
            }}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve Selected ({selectedOrders.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OMSAutomationPage;
